import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import AnalyticsService from '../../shared/services/AnalyticsService';

import ProviderWebsiteLink from './ProviderWebsiteLink';

const metersToMiles = (meters) => Math.round(meters * 0.00621371) / 10;

export const numberOfPages = (resultsCount, resultsLimit) => Math.ceil(resultsCount / resultsLimit);

function ProviderList(props) {
  const {
    results,
    resultsLimit,
    currentPage,
    hoveredResultId,
    selectedResultId,
    setPage,
    setUserCoordinates,
    setHoveredResultId,
    setSelectedResultId,
  } = props;

  const { t } = useTranslation();
  const firstResult = currentPage * resultsLimit;
  const lastResult = firstResult + resultsLimit;

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <div>
      <ol className='find-a-doctor__provider-list'>
        {
          results.slice(firstResult, lastResult).map((result, i) => {
            const distanceInMiles = metersToMiles(result.location.distance);
            /* eslint-disable eqeqeq */
            const isActive = result.id == selectedResultId || result.id == hoveredResultId;
            /* eslint-enable eqeqeq */

            return (
              <li
                className={`find-a-doctor__provider-list__result ${isActive ? 'find-a-doctor__provider-list__result--active' : ''}`}
                key={i}
                onMouseEnter={() => {
                  setHoveredResultId(result.id);
                }}
                onMouseLeave={() => {
                  setHoveredResultId(null);
                }}
                onClick={() => {
                  AnalyticsService.sendClick(
                    'Find a Doctor',
                    `[${result.id}] - [${result.name}]`,
                  );
                  setUserCoordinates({
                    latitude: result.location.latitude,
                    longitude: result.location.longitude,
                  });
                  setSelectedResultId(result.id);
                }}
              >
                <div className="find-a-doctor__provider-list__result__details">
                  <div className="find-a-doctor__provider-list__result__heading viz-text viz-text--accent">
                    <div className="find-a-doctor__provider-list__result__number">
                      { i + 1 + firstResult }.
                    </div>
                    <div className="find-a-doctor__provider-list__result__practice-name">
                      { result.practice_name }
                    </div>
                  </div>
                  <div className="find-a-doctor__provider-list__result__indent viz-text--accent">
                    { result.name }<br />
                    { result.location.address }<br />
                    { result.location.city },&nbsp;
                    { result.location.region }&nbsp;
                    { result.location.postal }<br />
                    { result.location.phone }<br />
                    {
                      result.promoted ? (
                        <Fragment>
                          <div className="find-a-doctor__provider-list__result__sponsored">
                            { t('provider_list.promoted_marker') }
                          </div>
                        </Fragment>
                      ) : (
                        <br />
                      )
                    }
                    {
                      (result.location.website || result.website) && (
                        <section>
                          <ProviderWebsiteLink
                            provider={result}
                            className='viz-btn viz-btn--primary viz-btn--sm find-a-doctor__provider-list__result__website'
                          >
                            { t('provider_list.website') }
                          </ProviderWebsiteLink>
                        </section>
                      )
                    }
                  </div>
                </div>
                <div className="find-a-doctor__provider-list__result__distance">
                  { `${distanceInMiles} ${distanceInMiles === 1 ? t('provider_list.singular_distance_term') : t('provider_list.plural_distance_term')}` }
                </div>
              </li>
            );
          })
        }
      </ol>
      <footer>
        <ol className='find-a-doctor__pagination'>
          {
            /* eslint-disable eqeqeq */
            results.length > resultsLimit &&
              [...Array(numberOfPages(results.length, resultsLimit))].map((x, i) => (
                <li
                  key={i}
                  onClick={() => {
                    setPage(i);
                  }}
                  role="button"
                  tabIndex={0}
                  className={`
                    find-a-doctor__pagination__item
                    ${i == currentPage ? 'find-a-doctor__pagination__item--active' : ''}
                  `}
                >
                  { i + 1 }
                </li>
              ))
            /* eslint-enable eqeqeq */
          }
        </ol>
      </footer>
    </div>
  );
  /* eslint-enable jsx-a11y/no-static-element-interactions */
}

ProviderList.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      opened: PropTypes.bool,
      website: PropTypes.string,
      practiceName: PropTypes.string,
      location: PropTypes.shape({
        city: PropTypes.string,
        phone: PropTypes.string,
        region: PropTypes.string,
        postal: PropTypes.string,
        address: PropTypes.string,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        distance: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ),
  resultsLimit: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  hoveredResultId: PropTypes.number,
  selectedResultId: PropTypes.number,
  setPage: PropTypes.func.isRequired,
  setUserCoordinates: PropTypes.func.isRequired,
  setHoveredResultId: PropTypes.func.isRequired,
  setSelectedResultId: PropTypes.func.isRequired,
};

export default ProviderList;
